<template>
    <div class="edit-in-place-wrapper" :class="mainWrapperStyle" ref="editInPlace">
        <div class="edit-in-place-modal" v-if="activeMode" @click="handleClickModal"></div>
        <div class="edit-in-place" :class="outerWrapperStyle" @mouseover="handleMouseOnWrapper" @mouseout="handleMouseOffWrapper" @click="handleClickWrapper">
            <div class="inner-shadow" :class="innerWrapperStyle">
                <div class="display-mode">
                    <div class="display-mode-icon" v-if="unSaved || unSynced"><div class="edit-in-place-icon">!</div></div>
                    <display-mode :ghost-text="isTextGhosted"
                                  :type="type"
                                  :display-value="adjustedDisplayValue">
                    </display-mode>

                    <svg xmlns="http://www.w3.org/2000/svg" class="svg-right-float" viewBox="0 0 512 512" v-if="hoverMode && !readOnly">
                        <desc>Pencil Icon</desc>
                        <path fill="#00B0D8" d="M497.9 142.1l-46.1 46.1c-4.7 4.7-12.3 4.7-17 0l-111-111c-4.7-4.7-4.7-12.3 0-17l46.1-46.1c18.7-18.7 49.1-18.7 67.9 0l60.1 60.1a47.92 47.92 0 010 67.9zM284.2 99.8L21.6 362.4.4 483.9c-2.9 16.4 11.4 30.6 27.8 27.8l121.5-21.3 262.6-262.6c4.7-4.7 4.7-12.3 0-17l-111-111c-4.8-4.7-12.4-4.7-17.1 0zM124.1 339.9c-5.5-5.5-5.5-14.3 0-19.8l154-154c5.5-5.5 14.3-5.5 19.8 0s5.5 14.3 0 19.8l-154 154c-5.5 5.5-14.3 5.5-19.8 0zM88 424h48v36.3l-64.5 11.3-31.1-31.1L51.7 376H88v48z"/>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" class="svg-right-float" viewBox="0 0 512 512" v-if="hoverMode && readOnly">
                        <desc>Lock Icon</desc>
                        <path fill="#8C8D8D" d="M400 224h-24v-72C376 68.2 307.8 0 224 0S72 68.2 72 152v72H48c-26.5 0-48 21.5-48 48v192c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48zm-104 0H152v-72c0-39.7 32.3-72 72-72s72 32.3 72 72v72z"/>
                    </svg>

                    <active-mode v-show="activeMode"
                                 ref="activeContainer"
                                 :ghost-text="isTextGhosted"
                                 :type="type"
                                 :active-value="adjustedActiveValue"
                                 :options="options"
                                 :rows="rows"
                                 :master-value="adjustedActiveMasterValue"
                                 :show-preview-master-value="showPreviewMasterValue"
                                 :show-save-and-sync="showSaveAndSync"
                                 :show-schedule="showSchedule"
                                 :un-synced="unSynced"
                                 :un-saved="unSaved"
                                 :null-select="nullSelect"
                                 @value-change="handleLocalValueChange"
                                 @entered-pressed="handleEnterPressed"
                                 @save="handleSaveRequest"
                                 @schedule="handleScheduleRequest"
                                 @cancelled="handleCancel"
                                 class="active-mode active-outer edit-in-place-top"
                                 >
                    </active-mode>

                </div>

                <!-- slot that holds the API handler object -->
                <slot></slot>

            </div>
        </div>
    </div>
</template>

<script>
/*

__Props__
readOnly:               true | false. can the user click on the value and edit it.
useMultiSelectRoleType: true | false. if true the user will see 'ALL' when no multi-select are selected (used in the Roles section of the PIM)
nullSelect:             true | false. (default true). single select types allow users to select nothing.
propertyValue:          if the type is multi-select the property value will be an array. otherwise it should be a string.
rows:                   how many multi/single select items should be shown before pagination (default 8)
type:                   short-text | long-text | single-select | multi-select | date | datetime | json | markdown
options:                available selectable options for single/multi select types
showSaveAndSync:        true | false. show the save&sync button when editing
masterValue:            the value is shown to the user when a non-master version is being edited. If this value is blank no preview will be shown
isMaster:               true | false. is the user editing the master version

__Modes__
display:                default starting mode.
hover:                  mouse is over element but the user has not clicked
active:                 user has clicked on this element and the user can interact with the element

__States__
stable:                 data has not been changed.
un-saved:               data has been changed but not saved or synced
un-synced:              data has been saved but not yet synced
saving:                 the changed data has been sent to the backend and a reply is still awaiting (this is the same when a sync is happening)

__Form Input/Editing Types__
short-text:             single line input box
long-text:              text area with no mark support
single-select:          a drop down box that allows you to select one value
multi-select:           a drop down box that allows you to select one or more values
date:                   select a date
datetime:               select a date or time
json:                   formated JSON editor with error and warning feedback
markdown:               formated text area with markdown support
html:                   formated text area with markdown & html support

*/

import Vue from "vue";

import DisplayModeValue from "./display-mode-value.vue";
import ActiveMode from "./active-mode.vue";

export default {
    name: "edit-in-place",
    components: {
        "display-mode": DisplayModeValue,
        "active-mode": ActiveMode
    },
    props: {
        readOnly: Boolean,
        useMultiSelectRoleType: {
            type: Boolean,
            default: false
        },
        nullSelect: {
            type: Boolean,
            default: true
        },
        propertyValue: {
            type: [Array, String, Number]
        },
        rows: {
            type: Number,
            default: 8
        },
        type: String,
        options: Array,
        showSaveAndSync: {
            type: Boolean,
            default: true
        },
        masterValue: {
            type: [Array, String, Number]
        },
        isMaster: {
            type: Boolean,
            default: false
        },
        showSchedule: {
            type: Boolean,
            default: false
        }
    },
    mounted() {
        this.apiHandler.$on("in-progress", () => {
            this.state = "saving";
        });
        this.apiHandler.$on("complete", () => {
            this.state = "stable";
        });
        this.apiHandler.$on("error", () => {
            this.state = "stable";
        });
    },
    watch: {
        propertyValue(newValue) {
            this.localValue = newValue;
            this.savedValue = newValue;
        },
        activeMode(newValue) {
            if (newValue) {
                if (this.type === "markdown") {
                    if (this.initMarkDown) {
                        this.$nextTick(() => {
                            // this needs to be manually initialized otherwise content doesn't appear until it is clicked on.
                            this.$refs.activeContainer.$refs.markdownEditor.initialize();
                            this.initMarkDown = false;
                        });
                    } else {
                        this.$nextTick(() => {
                            // need to refresh the content and re-focus.
                            this.$refs.activeContainer.$refs.markdownEditor.easymde.codemirror.refresh();
                            this.$refs.activeContainer.$refs.markdownEditor.easymde.codemirror.focus();
                        });
                    }
                }
                if (this.type === "json" && this.initJson) {
                    this.$nextTick(() => {
                        // this needs to be manually initialized otherwise content doesn't appear until it is clicked on.
                        this.$refs.activeContainer.$refs.jsonEditor.refresh();
                        this.initJson = false;
                    });
                }
                if (this.type === "date" || this.type === "datetime") {
                    this.$refs.activeContainer.$refs.dateinput.pickerOpen = true;
                }
            }
        }
    },
    data() {
        return {
            containerWidth: 0,
            mode: "display",
            state: "stable",
            initJson: true,
            initMarkDown: true,
            localValue: this.getClonedPropertyValue(),
            savedValue: this.getClonedPropertyValue()
        };
    },
    computed: {
        isTextGhosted() {
            // true if
            // -- locked
            // -- the selected version is NOT master && local value is null or underfined

            if (this.readOnly) {
                return true;
            }

            if (this.type === "single-select" &&
                !this.isMaster &&
                (this.masterValue == this.localValue)) {
                return true
            }

            return (
                !this.isMaster &&
                (this.localValue === null || this.localValue == "")
            );
        },
        showPreviewMasterValue() {
            return (
                !this.isMaster &&
                this.masterValue !== undefined &&
                this.masterValue !== null
            );
        },
        mainWrapperStyle() {
            let styles = {};
            //no modification needed ... yet
            return styles;
        },
        adjustedActiveValue() {
            if (this.type == "multi-select") {
                return this.localValue;
            }

            if (this.type == "single-select") {
                return this.convertSingleSelectValueToLabel(
                    this.getSingleSelectValue()
                );
            }

            return this.adjustedDisplayValue;
        },
        adjustedActiveMasterValue() {
            // this is what is shown to the user as the default master value when another version is selected.
            if (this.type == "multi-select") {
                if(typeof this.masterValue === 'string')
                {
                    return this.getMultiSelectionValuesFrom(this.masterValue.split(','));
                }
                else
                {
                    return this.getMultiSelectionValuesFrom(this.masterValue);
                }
            }

            if (this.type == "single-select") {
                return this.convertSingleSelectValueToLabel(this.masterValue);
            }

            return this.masterValue;
        },
        adjustedDisplayValue() {
            if (this.type == "multi-select") {
                return this.getMultiSelectionValuesFrom(this.localValue);
            }

            if (this.type == "single-select") {
                return this.convertSingleSelectValueToLabel(
                    this.getSingleSelectValue()
                );
            }

            if (this.localValue !== "" && this.localValue !== null) {
                return this.localValue;
            }

            if (this.isMaster) {
                if (this.localValue === null) {
                    return "";
                }
                return this.localValue;
            }

            //Version set is NOT master and the property value is null
            if (this.propertyValue === null || this.propertyValue === "") {
                if (this.masterValue === null) {
                    return "";
                }
                return this.masterValue;
            }

            return this.localValue;
        },
        outerWrapperStyle() {
            // calculate css styles depending on the current state of the component
            let styles = {};

            if (this.hoverMode && !this.readOnly) {
                styles["active-outer"] = true;
                styles["hover-outer"] = true;
            }
            if (this.hoverMode && this.readOnly) {
                styles["hover-outer-locked"] = true;
            }

            if (this.activeMode) {
                styles["active-outer"] = true;
            }

            if (this.unSaved) {
                styles["display-unsaved-outer"] = true;
            }

            return styles;
        },
        innerWrapperStyle() {
            // calculate css styles depending on the current state of the component
            let styles = {};

            if (this.hoverMode || this.activeMode) {
                if (!this.readOnly) {
                    styles["active-inner"] = true;
                }
            }

            if (this.readOnly) {
                styles["locked-inner"] = true;
            }

            if (this.unSaved) {
                styles["display-unsaved-inner"] = true;
            }

            if (this.hoverMode || this.displayMode) {
                styles["inner-shadow-alignment"] = true;
            }

            return styles;
        },
        apiHandler() {
            return this.$slots.default[0].componentInstance;
        },
        displayMode() {
            return this.mode == "display";
        },
        hoverMode() {
            return this.mode == "hover";
        },
        activeMode() {
            return this.mode == "active";
        },
        unSynced() {
            return this.state == "unSynced";
        },
        unSaved() {
            if (Array.isArray(this.localValue)) {
                return !_.isEqual(this.localValue, this.savedValue);
            }

            if (
                this.isEmpty(this.savedValue) &&
                this.isEmpty(this.localValue)
            ) {
                return false;
            }
            return this.parseHTML(this.localValue) != this.parseHTML(this.savedValue);
        }
    },
    methods: {
        parseHTML(string){
            const parsed = new DOMParser().parseFromString(string, "text/html");
            
            return parsed.documentElement.textContent;
        },
        isEmpty(value) {
            return value == null || value == "";
        },
        getMultiSelectionValuesFrom(value) {
            if (!Array.isArray(value)) {
                if (this.useMultiSelectRoleType) {
                    return ["All"];
                }
                return [];
            }

            let selectedValues = [];

            for (let i = 0; i < value.length; i++) {
                // Check to see if the ID stored in the localValue exist in the available option.
                // add it to the selectedValue if it does
                let options = this.options.filter(optionsElement => {
                    return (
                        parseInt(optionsElement.id, 10) ===
                        parseInt(value[i], 10)
                    );
                });

                if (options.length > 0) {
                    selectedValues.push(options[0].name);
                }
            }

            if (selectedValues.length === 0) {
                if (this.useMultiSelectRoleType) {
                    return ["All"];
                }
                return [];
            }

            return selectedValues;
        },
        getSingleSelectValue() {
            let value = "";

            if (this.isMaster) {
                if (this.localValue !== "" && this.localValue !== null) {
                    value = this.localValue;
                }
            } else {
                if (this.localValue === null || this.localValue === "") {
                    if (this.masterValue !== null && !this.nullSelect) {
                        value = this.masterValue;
                    } 
                } else {
                    value = this.localValue;
                }
            }

            return value;
        },
        convertSingleSelectValueToLabel(value) {
            let label = this.options.filter(option => {
                return option.value === value;
            });

            if (label.length === 0) {
                return "";
            }
            return label[0].label;
        },
        handleLocalValueChange(val) {
            if (this.type === "single-select") {
                let labelArray = this.options.filter(el => {
                    return el.label === val;
                });

                if (this.localValue == labelArray[0].value) {
                    this.localValue = "";
                } else {
                    this.localValue = labelArray[0].value;
                }
            } else if (this.type === "multi-select") {
                val = val.id;

                //clone localValue to prevent pass-by-reference array mayhem
                let tempValue = this.localValue.slice(0);

                //check to see if value exists in array ... if so remove it
                //if not then add it.
                let checklist = tempValue.filter(id => {
                    return id == val;
                });

                if (checklist.length == 0) {
                    tempValue.push(val);
                } else {
                    tempValue = tempValue.filter(id => {
                        return !(id == val);
                    });
                }

                this.localValue = tempValue;
            } else {
                // string value.
                this.localValue = val;
            }
        },
        getClonedPropertyValue() {
            if (Array.isArray(this.propertyValue)) {
                return this.propertyValue.slice(0);
            }

            return this.propertyValue;
        },
        handleEnterPressed() {
            this.mode = "display";
        },
        handleCancel() {
            if (Array.isArray(this.savedValue)) {
                this.localValue = this.savedValue.slice(0);
            } else {
                this.localValue = this.savedValue;
            }
            this.initJson = true;
            this.mode = "display";
        },
        handleClickModal() {
            this.mode = "display";
        },
        handleClickWrapper() {
            if (this.hoverMode && !this.readOnly) {
                this.containerWidth = this.$refs.editInPlace.offsetWidth;
                this.mode = "active";
                this.$nextTick(() => {
                    this.$refs.activeContainer.$el.style.width =
                        this.containerWidth + "px";
                    if (
                        this.type == "short-text" ||
                        this.type == "number" ||
                        this.type === "long-text"
                    ) {
                        this.$refs.activeContainer.$refs.textInputElement.focus();
                    }
                });
            }
        },
        handleMouseOnWrapper() {
            if (this.displayMode) {
                this.mode = "hover";
            }
        },
        handleMouseOffWrapper() {
            if (this.hoverMode) {
                this.mode = "display";
            }
        },
        handleSaveRequest(withSync) {
            if (this.localValue != this.savedValue) {
                this.$emit("value-save", this.localValue);
                this.savedValue = this.localValue;
                if (withSync) {
                    this.apiHandler.handleNewValueWithSync(this.localValue);
                } else {
                    this.apiHandler.handleNewValue(this.localValue);
                }

                //throws an error when you try and update a prop. But it should be fine in this case - so ignoring error
                Vue.config.silent = true;
                this.propertyValue = this.localValue;
                Vue.config.silent = false;
            }

            this.handleClickModal();
        },
        handleScheduleRequest(date) {
            if (this.localValue != this.savedValue) {
                this.$emit("value-save", this.localValue);
                this.savedValue = this.localValue;
                this.apiHandler.scheduleNewValue(this.localValue, date);

                Vue.config.silent = true;
                this.propertyValue = this.localValue;
                Vue.config.silent = false;
            }

            this.handleClickModal();
        }
    }
};
</script>

<style lang="scss">
.display-mode {
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 15px;
    overflow: hidden;
    white-space: nowrap;

    .display-mode-value-wrapper {
        width: 100%;
    }

    .cloud-tag {
        display: flex;
        flex-wrap: wrap;

        .tag {
            font-size: 12px;
            height: 24px;
            padding: 0 10px;
            margin: 3px 6px 3px 0;
            border-radius: 2px;
            background-color: #1d4e7d;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            text-transform: uppercase;
        }

        .ghost-tag {
            background-color: #999999;
        }
    }

    input.static-edit-in-place-value {
        pointer-events: none;
        background-color: unset;
        width: 90%;
        border: none;
    }
    input.static-edit-in-place-value:focus {
        outline: none;
    }

    .display-mode-icon {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
    }
}

div.CodeMirror-lint-tooltip {
    z-index: 10000000;
}

.edit-in-place-wrapper {
    width: 100%;
    position: relative;
}

.edit-in-place {
    height: 100%;
    min-height: 38px;
    border: 1px solid #bfbcbc;
    cursor: initial;
    border-radius: 5px;
}

.edit-in-place-modal {
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    background-color: rgba($color: white, $alpha: 0.6);
    z-index: 20000;
}

.inner-shadow {
    border-color: #e9e9e9;
    border-top-style: solid;
    border-top-width: 1px;
    border-left-style: solid;
    border-left-width: 1px;
    height: 100%;
    min-height: 38px;
    width: 100%;
    background: linear-gradient(to right, white, #fcfcfc);
    border-radius: 5px;
}

.inner-shadow-alignment {
    display: flex;
    align-items: center;
}

.active-mode {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    min-width: 400px;

    .input-wrapper {
        .input-area {
            padding: 10px 15px 0 15px;
            input,
            textarea {
                width: 100%;
                border: none;
                border-bottom-style: dotted;
                border-bottom-width: 1px;
                border-bottom-color: #576670;
                outline: none;
                background-color: unset;
            }
            input:focus,
            textarea:focus {
                outline: none;
            }
        }
        .input-action-area {
            padding: 10px 15px 10px 15px;
            button {
                height: 30px;
                padding: 5px 25px;
            }
        }
        .input-message-wrapper {
            height: 35px;
            padding: 0 15px 10px 15px;
        }
        .input-message-area {
            border-style: solid;
            border-width: 1px;
            border-radius: 5px;
            height: 23px;
            background-color: white;
            display: flex;
            align-items: center;

            &.input-message-not-saved {
                border-color: #ab5755;
                color: #ab5755;
            }

            &.input-message-not-synced {
                border-color: #bfbcbc;
                color: #bfbcbc;
            }

            &.input-message-editor-active {
                border-color: #333;
                color: #333;
            }
        }
        .select-option {
            max-height: 150px;
            overflow-y: scroll;
        }
    }
}

.preview-master-value {
    padding: 0 0 15px 15px;
    color: #868686;
    .master-value-label {
        color: black;
    }
}

.ghost-text {
    color: #858585;
}

.edit-in-place-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    background-color: #ab5755;
    width: 12px;
    height: 100%;
    margin-right: 5px;
    font-size: 17px;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
}

.svg-right-float {
    height: 94%;
    position: absolute;
    right: 0;
    background-color: #f5fbfc;
    max-height: 32px;
    top: 2px;
}

.active-inner {
    border-color: #d0e7ec;
    background: linear-gradient(to right, white, #f5fbfc);
}

.locked-inner {
    background: unset;
    background-color: #f8f8f8;
}

.active-outer {
    border: 1px solid #00b0d8;
    border-radius: 5px;
}

.display-unsaved-inner {
    border-color: #e7dfdf;
    background: linear-gradient(to right, white, #fcfcfc);
}

.display-unsaved-outer {
    border: 1px solid #ab5755;
}

.edit-in-place-top {
    z-index: 20010;
    min-height: 100%;
}

.hover-outer {
    cursor: pointer;
}

.hover-outer-locked {
    cursor: initial;
}

textarea.static-edit-in-place-value {
    width: 100%;
    border: none;
    outline: none;
    background-color: unset;
}
</style>
<style scoped>
svg {
    width: 16px;
    height: 16px;
    height: 94%;
    margin-right: 4px;
    min-width: 16px;
}
</style>
